import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteCard from "@/components/feature/SiteCard";
import SortSelect from "@/components/feature/SortSelect";
import EmptyState from "@/components/feature/EmptyState";
import SearchBar from "@/components/feature/SearchBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import { searchSites, sortSites } from "@/utils/site";

const sortOptions = [
  { value: "default", label: "综合排序" },
  { value: "visits", label: "按访问量" },
  { value: "updated", label: "按更新时间" },
  { value: "name", label: "按字母顺序" },
];

const suggestions = ["淘宝", "微博", "视频", "翻译工具", "在线课程", "GitHub", "游戏平台"];

export default function Search() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [sort, setSort] = useState("default");

  const results = useMemo(() => sortSites(searchSites(keyword), sort), [keyword, sort]);

  usePageMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 网址导航` : "搜索网站 - 网址导航",
    description: keyword
      ? `在网址导航中搜索“${keyword}”，共找到 ${results.length} 个相关网站入口，支持按访问量与更新时间排序。`
      : "输入关键词，快速查找网址导航收录的热门网站与实用资源。",
    keywords: "网址导航,网站搜索,热门网站,网站入口",
  });

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="font-medium text-foreground-800">搜索结果</span>
      </nav>

      <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <h1 className="text-xl font-bold text-foreground-950 md:text-2xl">
          {keyword ? (
            <>
              搜索：<span className="text-primary-600">{keyword}</span>
            </>
          ) : (
            "搜索网站"
          )}
        </h1>
        <p className="mt-1.5 text-sm text-foreground-600">
          {keyword ? `共匹配到 ${results.length} 个网站，可修改关键词重新搜索。` : "输入关键词开始搜索吧。"}
        </p>
        <div className="mt-4 max-w-2xl">
          <SearchBar key={keyword} defaultValue={keyword} size="lg" />
        </div>
      </div>

      {keyword ? (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
          <p className="text-sm text-foreground-600">
            共 <span className="font-semibold text-primary-600">{results.length}</span> 个结果
          </p>
          <SortSelect value={sort} onChange={setSort} options={sortOptions} />
        </div>
      ) : null}

      {keyword && results.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            icon="ri-search-eye-line"
            title="没有找到相关网站"
            description="换一个关键词试试，或从下面的热门搜索开始，也可以返回首页浏览分类。"
            action={{ label: "返回首页", to: "/" }}
          />
          <div className="mt-6">
            <p className="mb-3 text-center text-sm text-foreground-600">试试这些热门搜索：</p>
            <div className="flex flex-wrap justify-center gap-2">
              {suggestions.map((s) => (
                <Link
                  key={s}
                  to={`/search?q=${encodeURIComponent(s)}`}
                  className="rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  {s}
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {keyword && results.length > 0 ? (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((site) => (
            <SiteCard key={site.id} site={site} />
          ))}
        </div>
      ) : null}

      {!keyword ? (
        <div className="mt-8">
          <p className="mb-3 text-sm text-foreground-600">热门搜索：</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <Link
                key={s}
                to={`/search?q=${encodeURIComponent(s)}`}
                className="rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                {s}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}